import React, { useRef, useState, DragEvent, ChangeEvent } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { abyssColors } from '@/theme/colors';
import { useWizard } from '../context/WizardContext';

export const SelectFiles = () => {
  const { selectedFiles, setSelectedFiles, goToNextStep } = useWizard();
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const newFiles = Array.from(incoming).filter((file) => file.type === 'application/pdf');
    if (newFiles.length === 0) return;
    setSelectedFiles([...selectedFiles, ...newFiles]);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files);
    // Reset so same files can be selected again
    event.target.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const removeFile = (index: number) => {
    const updated = [...selectedFiles];
    updated.splice(index, 1);
    setSelectedFiles(updated);
  };

  return (
    <Stack gap="2.4rem" height="100%">
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: `0.2rem dashed ${
            isDragging ? abyssColors.primary[500] : abyssColors.primary[200]
          }`,
          borderRadius: '0.8rem',
          padding: '4rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.2rem',
          cursor: 'pointer',
          backgroundColor: isDragging ? abyssColors.primary[100] : abyssColors.primary[50],
          transition: 'all 0.2s ease',
          minHeight: '20rem',
          minWidth: '70rem',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <UploadFileIcon sx={{ fontSize: '4rem', color: abyssColors.primary[300] }} />
        <Typography variant="body1" color="primary.600" fontWeight={500}>
          Drag & drop PDF files here
        </Typography>
        <Typography variant="body2" color="primary.400">
          or click to browse
        </Typography>
      </Box>

      {selectedFiles.length > 0 && (
        <Stack gap="0.8rem" sx={{ overflowY: 'auto', maxHeight: '28rem' }}>
          <Typography variant="body2" color="primary.600" fontWeight={500}>
            {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
          </Typography>
          {selectedFiles.map((file, index) => (
            <Stack
              key={`${file.name}-${index}`}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                border: `0.1rem solid ${abyssColors.primary[100]}`,
                borderRadius: '0.6rem',
                padding: '0.8rem 1.2rem',
              }}
            >
              <Stack direction="row" alignItems="center" gap="0.8rem">
                <UploadFileIcon sx={{ fontSize: '2rem', color: abyssColors.primary[400] }} />
                <Stack>
                  <Typography variant="body2" color="primary.700" fontWeight={500}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="primary.400">
                    {(file.size / 1_024 / 1_024).toFixed(2)} MB
                  </Typography>
                </Stack>
              </Stack>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  removeFile(index);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Stack direction="row" justifyContent="flex-end" marginTop="auto">
        <Button variant="contained" onClick={goToNextStep} disabled={selectedFiles.length === 0}>
          Next
        </Button>
      </Stack>
    </Stack>
  );
};
